// material-ui
import ReactDOMServer from 'react-dom/server';
import { useTheme } from '@mui/material/styles';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { DefaultAgGridTooltip } from '../../components/ag-grid/DefaultAgGridTooltip';
import MainCard from '../../components/MainCard';
import ScrollX from '../../components/ScrollX';
import {
  Backdrop,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  IconButton,
  OutlinedInput,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery
} from '@mui/material';
import useConfig from '../../hooks/useConfig';
import useAuth from '../../hooks/useAuth';

// AgGrid
import { AgGridReact } from 'ag-grid-react';
import {
  CellStyle,
  GridApi,
  ICellRendererParams,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  SideBarDef,
  ValueFormatterParams
} from 'ag-grid-enterprise';
import {
  ColumnMovedEvent,
  ColumnResizedEvent,
  ColumnVisibleEvent,
  SortChangedEvent,
  GetContextMenuItemsParams,
  GridReadyEvent,
  ToolPanelVisibleChangedEvent,
  PaginationChangedEvent,
  FilterModel,
  ColDef
} from 'ag-grid-community';
import 'ag-grid-enterprise/styles/ag-grid.css';
import 'ag-grid-enterprise/styles/ag-theme-material.css';
import 'assets/third-party/ag-theme-material-dark.scss';
import {
  Integration,
  Rights,
  SortArgs,
  Transaction,
  TransactionStatus,
  TransactionType,
  useCancelTransactionMutation,
  useDeleteManualTransactionMutation,
  useListTransactionsLazyQuery,
  useSaveTransactionTypeMutation,
  useStopTransactionMutation,
  useTransactionsSummaryLazyQuery,
  GridType
} from '../../graphql/types';
import { ExportOutlined, InteractionOutlined, SearchOutlined, RetweetOutlined, BarsOutlined, FilterOutlined, LinkOutlined } from '@ant-design/icons';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import PlatformCompanyLogo from '../../utils/platformCompanyLogo';
import { getFormattedDateUTC } from '../../utils/getFormattedDate';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { reportApi } from '../../api/reportApi';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { SearchIntegrationAutocomplete } from '../../sections/integrations/SearchIntegrationAutocomplete';
import { dispatch } from '../../store';
import { openSnackbar } from '../../store/reducers/snackbar';
import EditTransactionNote from '../../sections/merchants/EditTransactionNote';
import ActionConfirmation from '../../components/mca/ActionConfirmation';
import { underscoreToSpace } from '../../utils/strings';
import { McaStatusChip } from '../../sections/merchants/McaStatusChip';
import AddManualTransaction from '../../sections/merchants/AddManualTransaction';
import { getErrorMessage, requireMutationData } from '../../utils/graphqlMutationResult';
import {
  TransactionAmountCell,
  TransactionDefaultTypeCell,
  TransactionStatusCell
} from '../../components/ag-grid/AgGridTransactionsChipCell';
import { manualTransactions } from '../../sections/merchants/McaTransactions';
import { Icon, supportedTransactionTypesforIcons, supportedPaymentMethodIdsforIcons } from '../../components/icons';
import TransactionsTypeLogo from '../../utils/transactionsTypeLogo';
import PaymentMethodsLogo from '../../utils/paymentMethodLogo';
import TransactionsActionsCellRenderer from '../../components/TransactionsActionsCellRenderer';
import McaListTools from '../../sections/merchants/McaListTools';
import useAgGridOutsideClick from '../../components/ag-grid/useAgGridOutsideClick';
import { applyGridColumnState } from 'utils/agGridUtils';
import accountCellRenderer, { formatAccountNumber } from '../../components/ag-grid/AgGridAccountCellRenderer';
import CustomTooltip from '../../components/CustomTooltip';
dayjs.extend(utc);
interface TransactionFilterModel {
  search?: { filterType: string; type: string; filter: string };
  status?: { filterType: string; values: string[] };
  type?: { filterType: string; values: string[] };
  fromDate?: { filterType: string; type: string; filter: string };
  toDate?: { filterType: string; type: string; filter: string };
  integrationId?: { filterType: string; type: string; filter: string };
}
export const allowedEditableTransactionTypes = [
  TransactionType.NsfFee,
  TransactionType.Debit,
  TransactionType.DefaultFee,
  TransactionType.AchProgramFee,
  TransactionType.Collection,
  TransactionType.OtherFee
];
const TransactionsFilterContext = createContext<{
  filterState: FilterModel | null;
  setFilterState: (filters: FilterModel | null) => void;
}>({
  filterState: null,
  setFilterState: () => { }
});

export const TransactionsFilterProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [filterState, setFilterState] = useState<FilterModel | null>(null);
  return <TransactionsFilterContext.Provider value={{ filterState, setFilterState }}>{children}</TransactionsFilterContext.Provider>;
};
const TransactionsList = () => {
  const theme = useTheme();
  const { user: currentUser } = useAuth();
  const userId = currentUser?.id;
  const keyName = `${userId}_TRANSACTIONS_GRID`;
  const perPage = 50;
  const [rowsPerPage, setRowsPerPage] = useState<number>(parseInt(localStorage.getItem(keyName + '_ROWS_PER_PAGE_STATE') || '') || perPage);
  const useScroll = localStorage.getItem(keyName + '_USE_SCROLL') === 'false' ? null : true;
  const useCompactView = localStorage.getItem(keyName + '_USE_COMPACT_VIEW') === 'false' ? null : true;
  const isSuperAdmin = currentUser?.rights.includes(Rights.IsSuperAdmin);
  const { mode } = useConfig();
  const matchDownSM = useMediaQuery(theme.breakpoints.down('sm'));
  const gridStyle = useMemo(() => ({ height: '100%', width: '100%' }), []);
  const [currentTotalRows, setCurrentTotalRows] = useState<number>(0);
  const [searchValue, setSearchValue] = useState('');
  const [fromDateValue, setFromDateValue] = useState(dayjs().utc().subtract(3, 'month').toDate());
  const [toDateValue, setToDateValue] = useState(dayjs().utc().add(1, 'days').toDate());
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [backdropOpen, setBackdropOpen] = useState<boolean>(false);
  const canManage = currentUser?.rights.includes(Rights.ManageMcaTransactions) && currentUser?.rights.includes(Rights.EditMca);
  const [transaction, setTransaction] = useState<Transaction | null>(null);
  const [cancelTransaction] = useCancelTransactionMutation();
  const [deleteManualTransaction] = useDeleteManualTransactionMutation();
  const [stopTransaction] = useStopTransactionMutation();
  const [add, setAdd] = useState<boolean>(false);
  const DEFAULT_HEIGHT = 500;
  const ROW_HEIGHT = 47.77;
  const PADDING = 210;
  const MAX_HEIGHT = 800;
  const HEIGHT_OFFSET = 160;
  const [updateTransactionType] = useSaveTransactionTypeMutation();
  const [isFilterApplied, setIsFilterApplied] = useState<boolean>(false);
  const [refreshKey, setRefreshKey] = useState<number>(0);

  const [fetchData] = useListTransactionsLazyQuery({
    fetchPolicy: 'network-only'
  });
  const [fetchSummaryData] = useTransactionsSummaryLazyQuery({
    fetchPolicy: 'network-only'
  });
  const { filterState, setFilterState } = useContext(TransactionsFilterContext);
  const typeFilter = filterState?.type || '';
  const statusFilter = filterState?.status || '';
  const gridApiRef = useRef<GridApi>();
  const [editNote, setEditNote] = useState<boolean>(false);
  const handleEditNote = () => {
    setEditNote(!editNote);
    if (transaction && !editNote) setTransaction(null);
  };
  const [openCancelConfirmation, setOpenCancelConfirmation] = useState(false);
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
  const handleManualEdit = () => {
    setAdd(!add);
    if (transaction && !add) setTransaction(null);
  };
  const McaNoTransactionListStatusChipCellRenderer = ({ node, data }: ICellRendererParams) => {
    if (node.isRowPinned()) {
      return null;
    }

    return (
      <McaStatusChip
        id={data.mca.id}
        status={data.mca.status}
        label={data.mca.number}
        navigateToDetails={true}
        openOnSameContext={false}
        useCompactViewStyle={useCompactView ? true : false}
      />
    );
  };
  const handleCancelTransaction = async (approved: boolean) => {
    setOpenCancelConfirmation(false);
    if (!approved || !transaction) {
      return;
    }
    setBackdropOpen(true);
    try {
      if (manualTransactions.includes(transaction.type)) {
        const result = await cancelTransaction({
          variables: { id: transaction?.id }
        });
        requireMutationData(result, (data) => data?.cancelTransaction, 'Transaction was not canceled.');
      } else {
        const result = await stopTransaction({
          variables: {
            transactionId: transaction?.id
          }
        });
        requireMutationData(result, (data) => data?.stopTransaction, 'Transaction was not canceled.');
      }
      if (gridApi) {
        gridApi.refreshServerSide();
      }
      dispatch(
        openSnackbar({
          open: true,
          message: 'Transaction canceled successfully.',
          variant: 'alert',
          alert: { color: 'success' },
          close: true
        })
      );
    } catch (error) {
      dispatch(
        openSnackbar({
          open: true,
          message: getErrorMessage(error, 'Failed to cancel transaction.'),
          variant: 'alert',
          alert: { color: 'error' },
          close: true
        })
      );
    } finally {
      setBackdropOpen(false);
    }
  };
  const onFilterChanged = useCallback(() => {
    if (!gridApiRef.current) return;
    const filterModel = gridApiRef.current.getFilterModel();
    setFilterState(filterModel);
    localStorage.setItem(keyName + '_FILTER_STATE', JSON.stringify(filterModel));
  }, [keyName]);
  const handleDeleteTransaction = async (approved: boolean) => {
    setOpenDeleteConfirmation(false);
    if (!approved || !transaction) {
      return;
    }
    setBackdropOpen(true);
    try {
      const result = await deleteManualTransaction({
        variables: { id: transaction?.id }
      });
      requireMutationData(result, (data) => data?.deleteManualTransaction, 'Transaction was not deleted.');
      if (gridApi) {
        gridApi.refreshServerSide();
      }
      dispatch(
        openSnackbar({
          open: true,
          message: 'Transaction deleted successfully.',
          variant: 'alert',
          alert: { color: 'success' },
          close: true
        })
      );
    } catch (error) {
      dispatch(
        openSnackbar({
          open: true,
          message: getErrorMessage(error, 'Failed to delete transaction.'),
          variant: 'alert',
          alert: { color: 'error' },
          close: true
        })
      );
    } finally {
      setBackdropOpen(false);
    }
  };
  const getRowClass = (params: any) => {
    if (params.node.rowPinned === 'bottom') {
      return 'ag-pin-bottom-row-' + mode;
    }
  };
  const defaultColDef = useMemo(() => {
    return {
      suppressHeaderMenuButton: true,
      width: 90,
      editable: false,
      sortable: true,
      resizable: true,
      pivot: true,
      filter: false,
      wrapHeaderText: false,
      autoHeaderHeight: true,
      cellStyle: {
        display: 'flex',
        alignItems: 'center'
      },
      columnChooserParams: {
        suppressColumnFilter: true,
        contractColumnSelection: true
      },
      tooltipComponent: DefaultAgGridTooltip
    };
  }, []);
  const refreshGrid = useCallback(() => {
    if (gridApi) {
      gridApi.refreshServerSide();
    }
    setRefreshKey((prevKey) => prevKey + 1);
  }, [gridApi]);

  const sideBar = useMemo<SideBarDef>(() => {
    return {
      position: 'right',
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressColumnFilter: true,
            suppressColumnSelectAll: true,
            suppressColumnExpandAll: true
          }
        },
        {
          id: 'filters',
          labelDefault: 'Filters',
          labelKey: 'filters',
          iconKey: 'filter',
          toolPanel: 'agFiltersToolPanel',
          toolPanelParams: {
            suppressFilterSearch: true
          }
        },
        {
          id: 'settings',
          labelDefault: 'Settings',
          labelKey: 'settings',
          iconKey: 'menu',
          toolPanel: McaListTools,
          toolPanelParams: {
            preferencesKey: GridType.TransactionsGrid,
            displayCompactViewToggle: true,
            keyName,
            refreshGrid
          }
        }
      ]
    };
  }, []);
  const statusBar = useMemo(() => {
    return {
      statusPanels: [
        {
          statusPanel: 'agAggregationComponent',
          statusPanelParams: {
            aggFuncs: ['sum', 'min', 'max']
          }
        }
      ]
    };
  }, []);

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        width: 100,
        resizable: false,
        headerName: 'MCA#',
        cellClass: 'ag-centered-cell',
        field: 'mca.number',
        sortable: false,
        headerClass: 'ag-header-transaction-cell-center',
        toolPanelClass: ['ag-header-transaction-cell-center'],
        cellRenderer: McaNoTransactionListStatusChipCellRenderer,
        cellRendererParams: { useCompactViewStyle: useCompactView ? true : false }
      },
      {
        suppressColumnsToolPanel: !currentUser?.rights.includes(Rights.ViewMcaIntegration),
        initialHide: !currentUser?.rights.includes(Rights.ViewMcaIntegration),
        suppressHeaderMenuButton: true,
        headerName: 'Platform',
        headerClass: 'ag-header-transaction-cell-center',
        toolPanelClass: ['ag-header-transaction-cell-center'],
        headerComponent: () => {
          return (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              <Tooltip
                title="Platforms"
                PopperProps={{
                  sx: {
                    '& .MuiTooltip-tooltip': {
                      bgcolor: '#000000',
                      color: '#FFFFFF'
                    }
                  }
                }}
              >
                <InteractionOutlined style={{ fontSize: 20 }} />
              </Tooltip>
            </Box>
          );
        },
        field: 'integration.name',
        tooltipField: 'integration.name',
        cellClass: 'ag-centered-cell',
        cellRenderer: ({ data, node }: ICellRendererParams) => {
          if (node.isRowPinned()) {
            return null;
          }
          if (data?.type === TransactionType.Collection) {
            if (supportedPaymentMethodIdsforIcons.includes(data?.paymentMethod?.id)) {
              return (
                <CustomTooltip title={data?.paymentMethod?.id}>
                  <span className="agcell-align">
                    <Icon id={data?.paymentMethod?.id} size="xxs" />
                  </span>
                </CustomTooltip>
              );
            } else {
              return <PaymentMethodsLogo paymentMethod={data?.paymentMethod} size={'xs'} mt={useCompactView ? 0 : undefined} />;
            }
          }

          if (data.integration == null) {
            if (supportedTransactionTypesforIcons.includes(data?.type)) {
              return (
                <CustomTooltip title={data?.type}>
                  <span className="agcell-align">
                    <Icon type={data?.type} size="xxs" />
                  </span>
                </CustomTooltip>
              );
            } else {
              return <TransactionsTypeLogo type={data?.type} size={'xs'} mt={useCompactView ? 0 : undefined} />;
            }
          } else {
            return <PlatformCompanyLogo integration={data.integration as Integration} size={'xs'} mt={useCompactView ? 0 : undefined} />;
          }
        }
      },
      {
        headerName: 'Name',
        headerClass: 'ag-header-cell-merchant-group',
        toolPanelClass: ['ag-header-cell-merchant-group'],
        width: 220,
        field: 'name',
        tooltipField: 'name'
      },
      {
        initialHide: true,
        headerName: 'Origination Date',
        field: 'originationDate',
        cellStyle: { direction: 'rtl' } as CellStyle,
        headerClass: 'ag-header-performance-group-right',
        toolPanelClass: ['ag-header-performance-group-right'],
        width: 100,
        valueGetter: (params) => getFormattedDateUTC(params.data?.originationDate)
      },
      {
        headerName: 'Effective Date',
        width: 100,
        sort: 'desc',
        field: 'effectiveDate',
        headerClass: 'ag-header-performance-group-right',
        toolPanelClass: ['ag-header-performance-group-right'],
        cellStyle: { direction: 'rtl' } as CellStyle,
        valueGetter: (params) => getFormattedDateUTC(params.data?.effectiveDate)
      },
      {
        headerName: 'Type',
        field: 'type',
        filter: true,
        cellClass: 'ag-centered-cell',
        headerClass: 'text-center-lightskyblue',
        toolPanelClass: ['ag-header-performance-group-right'],
        cellRenderer: TransactionDefaultTypeCell,
        cellRendererParams: { useCompactViewStyle: useCompactView ? true : false },
        filterParams: {
          values: [
            TransactionType.Debit,
            TransactionType.Credit,
            TransactionType.Collection,
            TransactionType.Refund,
            TransactionType.Expense,
            TransactionType.AchProgramFee,
            TransactionType.OtherFee,
            TransactionType.DefaultFee,
            TransactionType.NsfFee

          ],
          suppressSorting: true,
          valueFormatter: ({ value }: ValueFormatterParams) => {
            return underscoreToSpace(value);
          }
        }
      },
      {
        headerName: 'Pmt Method',
        field: 'paymentMethod',
        tooltipValueGetter: (params) => {
          const id = params.data?.paymentMethod?.id;
          return id ? underscoreToSpace(id) : 'UNKNOWN';
        },
        sortable: false,
        headerClass: 'text-center-lightskyblue',
        toolPanelClass: ['ag-header-performance-group-right'],
        cellClass: 'ag-centered-cell',
        cellRenderer: ({ data, node }: ICellRendererParams) => {
          if (node.isRowPinned()) {
            return null;
          }
          if (supportedPaymentMethodIdsforIcons.includes(data?.paymentMethod?.id)) {
            return (
                <span className="agcell-align">
                  <Icon id={data?.paymentMethod?.id} size="xxs" />
                </span>
            );
          } else {
            return <PaymentMethodsLogo paymentMethod={data?.paymentMethod} size={'xs'} mt={useCompactView ? 0 : undefined} />;
          }
        }
      },
      {
        headerName: 'Account',
        width: 80,
        field: 'account',
        type: 'rightAligned',
        headerClass: 'ag-header-performance-group-right',
        toolPanelClass: ['ag-header-performance-group-right'],
        tooltipValueGetter: (params) => formatAccountNumber(params.value),
        cellRenderer: accountCellRenderer
      },
      {
        headerName: 'Status',
        field: 'status',
        filter: true,
        cellClass: 'ag-centered-cell',
        headerClass: 'text-center-lightskyblue',
        toolPanelClass: ['ag-header-performance-group-right'],
        cellRenderer: TransactionStatusCell,
        cellRendererParams: { useCompactViewStyle: useCompactView ? true : false },
        filterParams: {
          values: [
            TransactionStatus.Cleared,
            TransactionStatus.Nsf,
            TransactionStatus.Returned,
            TransactionStatus.Cancelled,
            TransactionStatus.Pending,
            TransactionStatus.Processed
          ]
        }
      },
      {
        headerName: 'Amount',
        field: 'amount',
        cellRenderer: TransactionAmountCell,
        cellRendererParams: { useCompactViewStyle: useCompactView ? true : false },
        width: 120,
        cellStyle: { direction: 'rtl' } as CellStyle,
        headerClass: 'ag-header-performance-group-right',
        toolPanelClass: ['ag-header-performance-group-right'],
      },
      {
        headerName: 'Deposit Date',
        width: 100,
        field: 'depositDate',
        headerClass: 'ag-header-performance-group-right',
        toolPanelClass: ['ag-header-performance-group-right'],
        cellStyle: { direction: 'rtl' } as CellStyle,
        valueGetter: (params) => getFormattedDateUTC(params.data?.depositDate)
      },
      { initialHide: true, headerName: 'Notes', headerClass: 'ag-header-performance-group-right', toolPanelClass: ['ag-header-performance-group-right'], field: 'notes', tooltipField: 'notes' },
      {
        initialHide: true,
        headerName: 'Return Date',
        width: 100,
        field: 'returnDate',
        headerClass: 'ag-header-fees-group-right',
        toolPanelClass: ['ag-header-fees-group-right'],
        cellStyle: { direction: 'rtl' } as CellStyle,
        valueGetter: (params) => getFormattedDateUTC(params.data?.returnDate)
      },
      {
        headerName: 'Return Code',
        field: 'returnCode',
        headerClass: 'lightsalmon',
        toolPanelClass: ['ag-header-fees-group-right'],
        width: 100
      },
      {
        initialHide: true,
        headerName: 'Transaction ID',
        headerClass: 'ag-header-transaction-cell-center',
        toolPanelClass: ['ag-header-transaction-cell-center'],
        headerComponent: () => {
          return (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              <CustomTooltip
                title="Transaction ID"
              >
                <LinkOutlined style={{ fontSize: 20 }} />
              </CustomTooltip>
            </Box>
          );
        },
        field: 'externalId',
        tooltipField: 'externalId'
      },
      {
        field: 'actions',
        headerName: '',
        hide: !canManage,
        initialHide: !canManage,
        headerComponent: () => {
          return (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              <CustomTooltip
                title="Actions"
              >
                <BarsOutlined style={{ fontSize: 20 }} />
              </CustomTooltip>
            </Box>
          );
        },
        cellRenderer: (params: ICellRendererParams) => (
          <TransactionsActionsCellRenderer
            data={params.data}
            node={params.node}
            setTransaction={setTransaction}
            handleEditNote={handleEditNote}
            handleManualEdit={handleManualEdit}
            setOpenCancelConfirmation={setOpenCancelConfirmation}
            setOpenDeleteConfirmation={setOpenDeleteConfirmation}
            isSuperAdmin={!!isSuperAdmin}
            manualTransactions={manualTransactions}
          />
        ),
        sortable: false,
        enablePivot: false,
        pivot: false,
        resizable: false,
        suppressNavigable: true,
        suppressHeaderMenuButton: true,
        aggFunc: null,
        suppressColumnsToolPanel: true,
        cellClass: 'no-border'
      },
      {
        field: 'search',
        hide: true,
        aggFunc: null,
        enablePivot: false,
        pivot: false,
        lockVisible: true,
        initialHide: true,
        suppressNavigable: true,
        suppressHeaderMenuButton: true,
        suppressColumnsToolPanel: true,
        suppressFiltersToolPanel: true,
        filter: 'agTextColumnFilter',
        filterParams: {
          newRowsAction: 'keep'
        }
      },
      {
        field: 'fromDate',
        hide: true,
        filter: 'agTextColumnFilter',
        lockVisible: true,
        initialHide: true,
        suppressNavigable: true,
        suppressHeaderMenuButton: true,
        suppressColumnsToolPanel: true,
        suppressFiltersToolPanel: true
      },
      {
        field: 'toDate',
        hide: true,
        filter: 'agTextColumnFilter',
        lockVisible: true,
        initialHide: true,
        suppressNavigable: true,
        suppressHeaderMenuButton: true,
        suppressColumnsToolPanel: true,
        suppressFiltersToolPanel: true
      },
      {
        field: 'integrationId',
        hide: true,
        filter: 'agTextColumnFilter',
        lockVisible: true,
        initialHide: true,
        suppressNavigable: true,
        suppressHeaderMenuButton: true,
        suppressColumnsToolPanel: true,
        suppressFiltersToolPanel: true
      }
    ],
    [useCompactView]
  );

  const { height, width } = useWindowDimensions();
  const [currentPageRows, setCurrentPageRows] = useState<number>(0);

  const gridHeight = useMemo<number>(() => {
    if (currentPageRows >= 1) {
      const maxGridHeight = height > MAX_HEIGHT ? height - HEIGHT_OFFSET : DEFAULT_HEIGHT;
      let calculatedHeight = PADDING;
      calculatedHeight += currentPageRows * ROW_HEIGHT;
      return Math.max(Math.min(calculatedHeight, maxGridHeight), DEFAULT_HEIGHT);
    }
    return DEFAULT_HEIGHT;
  }, [height, currentPageRows]);
  const onGridReady = useCallback(
    ({ api }: GridReadyEvent) => {
      setGridApi(api);
      gridApiRef.current = api;
      applyGridColumnState(api, keyName);

      let filterModel = null;

      if (filterState) {
        filterModel = filterState;
      } else if (filterModel?.status || filterModel?.type) {
        filterModel = {};

        if (statusFilter) {
          filterModel.status = {
            values: [statusFilter],
            filterType: 'set'
          };
        }

        if (typeFilter) {
          filterModel.type = {
            values: [typeFilter],
            filterType: 'set'
          };
        }
        if (filterModel.fromDate) {
          setFromDateValue(dayjs(filterModel.fromDate.filter).utc().toDate());
        }
        if (filterModel.toDate) {
          setToDateValue(dayjs(filterModel.toDate.filter).utc().toDate());
        }
      } else {
        const savedFilters = localStorage.getItem(keyName + '_FILTER_STATE');
        if (savedFilters) {
          filterModel = JSON.parse(savedFilters);
        }
      }

      if (filterModel) {
        api.setFilterModel(filterModel);
        setFilterState(filterModel);

        if (filterModel.search?.filter) {
          setSearchValue(filterModel.search.filter);
        }
      }
      api.addEventListener('filterChanged', onFilterChanged);
      gridApiRef.current = api;
      api.updateGridOptions({
        serverSideDatasource
      });
    },
    [gridApi, filterState]
  );
  useAgGridOutsideClick(gridApiRef.current);
  const onGridPreDestroyed = useCallback(() => {
    gridApiRef.current = undefined;
  }, []);
  useEffect(() => {
    return () => {
      gridApi?.removeEventListener('filterChanged', onFilterChanged);
    };
  }, [onFilterChanged]);
  useEffect(() => {
    if (gridApi) {
      const fm = gridApi.getFilterModel();
      if (fm['fromDate']) {
        const fromDate = fm['fromDate'].filter;
        setFromDateValue(dayjs(fromDate).utc().toDate());
      }
      if (fm['toDate']) {
        const toDate = fm['toDate'].filter;
        setToDateValue(dayjs(toDate).utc().toDate());
      }
    }
  }, [gridApi]);
  const onSearchChange = (value: string) => {
    if (!gridApi) {
      return;
    }
    setSearchValue(value);
    const fm = gridApi.getFilterModel();
    fm['search'] = { filterType: 'text', type: 'contains', filter: value };
    gridApi.setFilterModel(fm);
    gridApi.onFilterChanged();
  };

  const onFromDateChange = (value: string) => {
    if (!gridApi) {
      return;
    }
    setFromDateValue(dayjs(value).utc().toDate());
    const fm = gridApi.getFilterModel();
    fm['fromDate'] = { filterType: 'date', type: 'contains', filter: value };
    gridApi.setFilterModel(fm);
    gridApi.onFilterChanged();
    setFilterState({ ...filterState, fromDate: { filterType: 'date', type: 'contains', filter: value } });
  };

  const onToDateChange = (value: string) => {
    if (!gridApi) {
      return;
    }
    setToDateValue(dayjs(value).utc().toDate());
    const fm = gridApi.getFilterModel();
    fm['toDate'] = { filterType: 'date', type: 'contains', filter: value };
    gridApi.setFilterModel(fm);
    gridApi.onFilterChanged();
    setFilterState({ ...filterState, toDate: { filterType: 'date', type: 'contains', filter: value } });

  };

  const onIntegrationChange = (value: string) => {
    if (!gridApi) {
      return;
    }
    const fm = gridApi.getFilterModel();
    fm['integrationId'] = { filterType: 'text', type: 'contains', filter: value };
    gridApi.setFilterModel(fm);
    gridApi.onFilterChanged();
  };

  const onToolPanelVisibleChanged = (event: ToolPanelVisibleChangedEvent) => {
    if (!gridApi) {
      return;
    }
  };

  const pinnedBottomRowData = useMemo<any[]>(() => {
    return [
      {
        mca: { number: 'Total' },
        amount: 0
      }
    ];
  }, []);

  const serverSideDatasource: IServerSideDatasource = {
    getRows: async (params: IServerSideGetRowsParams) => {
      if (params.api.isDestroyed()) {
        return;
      }
      const filters = params.request.filterModel as TransactionFilterModel  || {};
      const hasStatusFilter = 'status' in filters && filters.status?.values?.length > 0;
      const hasTypeFilter = 'type' in filters && filters.type?.values?.length > 0;
      if (hasStatusFilter || hasTypeFilter) {
        setIsFilterApplied(true);
      } else setIsFilterApplied(false);
      const res = await fetchData({
        variables: {
          limit: (params.request.endRow || 0) - (params.request.startRow || 0),
          offset: params.request.startRow,
          search: filters?.search?.filter,
          statuses: filters?.status?.values,
          types: filters?.type?.values,
          integrationId: filters?.integrationId?.filter,
          fromDate: filters?.fromDate?.filter || dayjs(fromDateValue).format('YYYY-MM-DD'),
          toDate: filters?.toDate?.filter || dayjs(toDateValue).format('YYYY-MM-DD'),
          sortArgs: params.request.sortModel?.map((x) => ({ field: x.colId, direction: x.sort }) as SortArgs)
        }
      });
      if (params.api.isDestroyed()) {
        return;
      }
      applyGridColumnState(params.api, keyName);
      const rows = res.data?.listTransactions.data || [];
      const total = res.data?.listTransactions.total || 0;
      setCurrentTotalRows(total);
      const summaryRes = await fetchSummaryData({
        variables: {
          search: filters?.search?.filter,
          integrationId: filters?.integrationId?.filter,
          statuses: filters?.status?.values,
          types: filters?.type?.values,
          fromDate: filters?.fromDate?.filter || dayjs(fromDateValue).format('YYYY-MM-DD'),
          toDate: filters?.toDate?.filter || dayjs(toDateValue).format('YYYY-MM-DD')
        }
      });
      if (params.api.isDestroyed()) {
        return;
      }
      params.api.updateGridOptions({
        pinnedBottomRowData: [
          {
            mca: { number: 'Total' },
            ...summaryRes.data?.transactionsSummary
          }
        ]
      });
      setCurrentPageRows(rows.length);
      params.success({ rowData: rows, rowCount: total });
    }
  };

  const handleExportReport = useCallback(async () => {
    if (!gridApi) {
      return;
    }
    setBackdropOpen(true);
    const fm = gridApi.getFilterModel();
    const qp = {
      fromDate: fm?.fromDate?.filter || dayjs(fromDateValue).format('YYYY-MM-DD'),
      toDate: fm?.toDate?.filter || dayjs(toDateValue).format('YYYY-MM-DD'),
      statuses: fm?.status?.values,
      types: fm?.type?.values,
      search: fm?.search?.filter,
      integrationId: fm?.integrationId?.filter
    };
    await reportApi.handleExportReport('transactions', qp);
    setBackdropOpen(false);
  }, [gridApi]);
  const getContextMenuItems = (params: GetContextMenuItemsParams) => {
    const defaultItems = ['copy', 'export'];
    if (!params.node || !params.node.data) {
      return defaultItems;
    }
    const transactionTypes = [
      { name: 'DEBIT', type: TransactionType.Debit, show: params.node.data.type !== TransactionType.Debit },
      {
        name: 'ACH PROGRAM FEE',
        type: TransactionType.AchProgramFee,
        show: params.node.data.type !== TransactionType.AchProgramFee
      },
      { name: 'NSF FEE', type: TransactionType.NsfFee, show: params.node.data.type !== TransactionType.NsfFee },
      {
        name: 'DEFAULT FEE',
        type: TransactionType.DefaultFee,
        show: params.node.data.type !== TransactionType.DefaultFee
      }
    ];

    const createAction = (id: string, mcaId: string, type: TransactionType) => () => {
      updateTransactionType({ variables: { id, mcaId, type } });
      if (gridApi) {
        gridApi.refreshServerSide();
      }
    };

    const customItems = [
      {
        name: 'Change Type',
        icon: ReactDOMServer.renderToStaticMarkup(<RetweetOutlined />),
        subMenu: transactionTypes
          .filter(({ show }) => show)
          .map(({ name, type }) => ({
            name,
            action: createAction(params.node.data.id, params.node.data.mca.id, type)
          }))
      }
    ];
    return isSuperAdmin && allowedEditableTransactionTypes.includes(params.node.data.type)
      ? [...defaultItems, ...customItems]
      : defaultItems;
  };

  const onPageSizeChanged = useCallback((params: PaginationChangedEvent) => {
    if (!params.api.paginationGetPageSize() || useScroll) {
      return;
    }
    localStorage.setItem(keyName + '_ROWS_PER_PAGE_STATE', params.api.paginationGetPageSize() + '');
  }, []);

  const resetFilters = () => {
    if (gridApi) {
      gridApi.setFilterModel({
        status: null,
        type: null
      });
      gridApi.onFilterChanged();
    }
    setIsFilterApplied(false);
  };
  const saveColumnsStateOnChange = useCallback(
    (event: ColumnResizedEvent | SortChangedEvent | ColumnMovedEvent | ColumnVisibleEvent) => {
      if (
        gridApi && event.source?.toLowerCase().includes('ui') &&
        ((event.type === 'columnResized' && event.finished) ||
          (event.type === 'columnMoved' && event.finished) ||
          event.type === 'sortChanged' ||
          event.type === 'columnVisible')
      ) {
        if (event.type === 'columnVisible') {
          const colId = event.column?.getColId();
          if (colId && event.column?.isVisible()) {
            gridApi.autoSizeColumns([colId], true);
          }
        }
        const columnsState = JSON.stringify(gridApi.getColumnState());
        localStorage.setItem(keyName + '_COLUMNS_STATE', columnsState);
      }
    },
    [gridApi]
  );
  return (
    <MainCard content={false}>
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={backdropOpen}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <ScrollX>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Stack
            direction={matchDownSM ? 'column' : 'row'}
            spacing={1}
            justifyContent="space-between"
            alignItems="center"
            sx={{ pl: 3, pr: 3, pt: 1.5, pb: 1.5 }}
          >
            <Stack direction={matchDownSM ? 'column' : 'row'} spacing={1} alignItems="center">
              <Typography variant="caption" color="secondary">
                Platform
              </Typography>
              <SearchIntegrationAutocomplete
                value={filterState?.integrationId?.filter || null}
                onChange={(value: string | null) => {
                  onIntegrationChange(value);
                }}
              />
              <Typography variant="caption" color="secondary">
                Search
              </Typography>
              <OutlinedInput
                value={searchValue || ''}
                onChange={(e) => {
                  onSearchChange(e.target.value);
                }}
                placeholder={`Search ${currentTotalRows} records...`}
                startAdornment={<SearchOutlined />}
                size="small"
                sx={{ minWidth: '180px', pt: 0.125, pb: 0.125 }}
              />
              <Typography variant="caption" color="secondary">
                From
              </Typography>
              <DesktopDatePicker
                value={fromDateValue}
                onChange={(value) => {
                  onFromDateChange(dayjs(value).utc().format('YYYY-MM-DD'));
                }}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { width: 150, pt: 0.125, pb: 0.125 },
                    name: 'fromDate'
                  }
                }}
              />
              <Typography variant="caption" color="secondary">
                To
              </Typography>
              <DesktopDatePicker
                value={toDateValue}
                onChange={(value) => {
                  onToDateChange(dayjs(value).utc().format('YYYY-MM-DD'));
                }}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { width: 150, pt: 0.125, pb: 0.125 },
                    name: 'toDate'
                  }
                }}
              />
              {isFilterApplied && (
                <Tooltip
                  title={<Chip label="Reset filters" variant="combined" color="primary" />}
                  componentsProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        padding: 0
                      }
                    }
                  }}
                >
                  <IconButton onClick={resetFilters}>
                    <FilterOutlined />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
            <Stack direction={matchDownSM ? 'column' : 'row'} alignItems="center" spacing={1}>
              <CustomTooltip title="Export Report">
                <Button
                  variant="contained"
                  onClick={handleExportReport}
                  sx={{
                    fontSize: '12px',
                    minWidth: 'auto'
                  }}
                >
                  <ExportOutlined />
                </Button>
              </CustomTooltip>
            </Stack>
          </Stack>
        </LocalizationProvider>
        <Box style={{ height: `${gridHeight}px` }} display="flex" flexDirection="column">
          <div
            className={`${mode === 'light' ? 'ag-theme-material' : 'ag-theme-material-dark'} ${useCompactView ? 'compact-view' : 'expanded-view'
              }`}
            style={gridStyle}
          >
            <AgGridReact<Transaction>
              gridOptions={
                !localStorage.getItem(keyName + '_COLUMNS_STATE')
                  ? {
                    autoSizeStrategy: {
                      type: 'fitCellContents',
                      skipHeader: true
                    }
                  }
                  : {}
              }
              suppressColumnVirtualisation={true}
              key={refreshKey}
              onToolPanelVisibleChanged={onToolPanelVisibleChanged}
              sideBar={sideBar}
              statusBar={statusBar}
              columnDefs={columnDefs}
              getRowClass={getRowClass}
              defaultColDef={defaultColDef}
              onGridReady={onGridReady}
              onGridPreDestroyed={onGridPreDestroyed}
              pagination={!useScroll}
              rowModelType={'serverSide'}
              onColumnResized={saveColumnsStateOnChange}
              onSortChanged={saveColumnsStateOnChange}
              onColumnMoved={saveColumnsStateOnChange}
              onColumnVisible={saveColumnsStateOnChange}
              cellSelection={true}
              tooltipShowDelay={0}
              tooltipHideDelay={2000}
              paginationPageSize={rowsPerPage}
              cacheBlockSize={rowsPerPage}
              paginationPageSizeSelector={[50, 100, 500, 1000]}
              pinnedBottomRowData={pinnedBottomRowData}
              getContextMenuItems={getContextMenuItems}
              onPaginationChanged={onPageSizeChanged}
            ></AgGridReact>
          </div>
          <Dialog maxWidth="sm" fullWidth onClose={handleEditNote} open={editNote} sx={{ '& .MuiDialog-paper': { p: 0 } }}>
            {transaction && (
              <EditTransactionNote
                mca={transaction?.mca}
                entity={transaction}
                onCancel={handleEditNote}
                onSave={() => {
                  if (gridApi) {
                    gridApi.refreshServerSide();
                  }
                }}
              />
            )}
          </Dialog>
          <Dialog maxWidth="sm" fullWidth onClose={handleManualEdit} open={add} sx={{ '& .MuiDialog-paper': { p: 0 } }}>
            {add && (
              <AddManualTransaction
                mca={transaction?.mca}
                entity={transaction}
                onCancel={handleManualEdit}
                onSave={() => {
                  if (gridApi) {
                    gridApi.refreshServerSide();
                  }
                }}
              />
            )}
          </Dialog>
          {openCancelConfirmation && (
            <ActionConfirmation
              title={`Confirm transaction ${transaction?.id} canceling`}
              open={openCancelConfirmation}
              type="error"
              handleClose={handleCancelTransaction}
            ></ActionConfirmation>
          )}
          {openDeleteConfirmation && (
            <ActionConfirmation
              title={`Deleted transactions cannot be restored. Would you like to proceed?`}
              open={openDeleteConfirmation}
              useYesNoButtonLabels={true}
              type="error"
              handleClose={handleDeleteTransaction}
            ></ActionConfirmation>
          )}
        </Box>
      </ScrollX>
    </MainCard>
  );
};

export default TransactionsList;
